import * as React from 'react';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import { cn } from '@relay-towing/tokens';
import { uiMarker } from '../recipes/shared';

/**
 * A region that expands and collapses, with the trigger wired to it.
 *
 * Radix handles the part that is easy to get wrong: `aria-expanded` and
 * `aria-controls` on the trigger, and keeping the panel out of the tab order
 * while it is closed.
 *
 *   <Collapsible>
 *     <CollapsibleTrigger asChild><Button>Filters</Button></CollapsibleTrigger>
 *     <CollapsibleContent>…</CollapsibleContent>
 *   </Collapsible>
 */
export const Collapsible = CollapsiblePrimitive.Root;
export const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

export const CollapsibleContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.CollapsibleContent>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.CollapsibleContent>
>(function CollapsibleContent({ className, ...props }, ref) {
  return (
    <CollapsiblePrimitive.CollapsibleContent
      ref={ref}
      className={cn(
        uiMarker,
        // Radix publishes the panel's measured height as a CSS variable, which
        // is what makes a height animation possible at all — you cannot
        // transition to `auto`.
        'overflow-hidden',
        'data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up',
        className
      )}
      {...props}
    />
  );
});
